/**
 * 출석 전용 모드 (키오스크) — 태블릿에 두고 학생이 자율 등원/퇴실
 * 전화번호 뒷자리 4자리 입력 → 이름 확인 → [등원]/[퇴실]
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose, IoCheckmarkCircle, IoBackspace } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useDialog } from '../../context/DialogContext';
import { useAcademy } from '../../context/AcademyContext';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '←'];

const KioskPage = () => {
  const { aid } = useParams();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { setMode } = useAcademy();

  const exitToBiz = () => {
    setMode('academy');
    navigate('/app/biz', { replace: true });
  };

  const [academy, setAcademy] = useState(null);
  const [input, setInput] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitPin, setExitPin] = useState('');
  const [exitError, setExitError] = useState('');

  useEffect(() => { academyService.getAcademy(aid).then(setAcademy); }, [aid]);

  // 4자리 입력 → 매칭 학생 자동 검색
  useEffect(() => {
    if (input.length !== 4) { setCandidates([]); setSelected(null); return undefined; }
    let alive = true;
    academyService.findStudentsByPhoneLast4(aid, input).then((list) => {
      if (!alive) return;
      setCandidates(list);
      if (list.length === 1) setSelected(list[0]);
    });
    return () => { alive = false; };
  }, [aid, input]);

  const press = (k) => {
    if (toast || busy) return;
    if (k === 'C') { setInput(''); return; }
    if (k === '←') { setInput((p) => p.slice(0, -1)); return; }
    if (input.length >= 4) return;
    setInput((p) => p + k);
  };

  const reset = () => {
    setInput(''); setCandidates([]); setSelected(null); setToast(null);
  };

  const onMark = async (kind) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const r = await academyService.kioskMarkAttendance(aid, selected.id, kind);
      const label = kind === 'in' ? '등원' : '퇴실';
      const msg = r.classes === 0
        ? `${selected.name}님은 클래스 배정이 안 돼 있어요`
        : `${selected.name}님 ${label} ${r.time}`;
      setToast({ ok: r.classes > 0, msg });
      setTimeout(reset, 2200);
    } catch (e) {
      console.error('출석 기록 실패:', e);
      setToast({ ok: false, msg: '출석 기록에 실패했어요' });
      setTimeout(reset, 2200);
    } finally {
      setBusy(false);
    }
  };

  const onExit = () => {
    if (!academy?.kioskPin) { exitToBiz(); return; }
    setExitPin('');
    setExitError('');
    setExitOpen(true);
  };

  const pinRefs = useRef([]);
  const pinLength = (academy?.kioskPin || '').length || 4;

  // 다이얼로그 열릴 때 첫 칸 포커스
  useEffect(() => {
    if (!exitOpen) return;
    setTimeout(() => pinRefs.current[0]?.focus(), 50);
  }, [exitOpen]);

  const onPinChange = (i, raw) => {
    const d = (raw.match(/\d/g) || []).pop() || '';
    setExitError('');
    if (!d) {
      setExitPin((p) => p.slice(0, i));
      return;
    }
    const before = exitPin.slice(0, i);
    const next = (before + d).slice(0, pinLength);
    setExitPin(next);
    if (next.length < pinLength) {
      setTimeout(() => pinRefs.current[next.length]?.focus(), 0);
    } else {
      const target = academy?.kioskPin || '';
      setTimeout(() => {
        if (next === target) {
          exitToBiz();
        } else {
          setExitError('PIN이 일치하지 않아요');
          setExitPin('');
          pinRefs.current[0]?.focus();
        }
      }, 120);
    }
  };

  return (
    <Wrap>
      <Top>
        <TopTitle>{academy?.name || '출석 전용 모드'}</TopTitle>
        <ExitBtn type="button" onClick={onExit}>
          <IoClose size={22} /> 종료
        </ExitBtn>
      </Top>

      <Body>
        <Hint>부모님 전화번호 뒷자리 4자리</Hint>

        <Display>
          {[0, 1, 2, 3].map((i) => (
            <Dot key={i} $on={input.length > i}>{input[i] || ''}</Dot>
          ))}
        </Display>

        {input.length === 4 && (() => {
          if (candidates.length === 0) {
            return <NoMatch>등록되지 않은 번호예요</NoMatch>;
          }
          if (candidates.length > 1 && !selected) {
            return (
              <ChoiceList>
                <ChoiceTitle>본인을 선택해 주세요</ChoiceTitle>
                {candidates.map((c) => (
                  <ChoiceBtn key={c.id} type="button" onClick={() => setSelected(c)}>
                    {c.name}
                  </ChoiceBtn>
                ))}
              </ChoiceList>
            );
          }
          return (
            <Matched>
              <MatName>{selected?.name}</MatName>
              <Actions>
                <ActBtn type="button" disabled={busy} $kind="in" onClick={() => onMark('in')}>등원</ActBtn>
                <ActBtn type="button" disabled={busy} $kind="out" onClick={() => onMark('out')}>퇴실</ActBtn>
              </Actions>
            </Matched>
          );
        })()}

        <Pad>
          {KEYS.map((k) => (
            <Key
              key={k}
              type="button"
              $special={k === 'C' || k === '←'}
              onClick={() => press(k)}
            >
              {k === '←' ? <IoBackspace size={28} /> : k}
            </Key>
          ))}
        </Pad>
      </Body>

      {toast && (
        <Toast $ok={toast.ok}>
          {toast.ok && <IoCheckmarkCircle size={32} />}
          <ToastMsg>{toast.msg}</ToastMsg>
        </Toast>
      )}

      {exitOpen && (
        <ExitOverlay onClick={() => setExitOpen(false)}>
          <ExitBox onClick={(e) => e.stopPropagation()}>
            <ExitTitle>해제 PIN 입력</ExitTitle>
            <PinInputs>
              {Array.from({ length: pinLength }).map((_, i) => (
                <PinInput
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  ref={(el) => { pinRefs.current[i] = el; }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={exitPin[i] || ''}
                  onChange={(e) => onPinChange(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !exitPin[i] && i > 0) {
                      e.preventDefault();
                      pinRefs.current[i - 1]?.focus();
                      setExitPin((p) => p.slice(0, i - 1));
                    }
                  }}
                />
              ))}
            </PinInputs>
            <PinHint>운영홈에서 설정한 해제 PIN을 입력해야 출석 모드를 종료할 수 있어요.</PinHint>
            {exitError && <ExitError>{exitError}</ExitError>}
            <ExitCancel type="button" onClick={() => setExitOpen(false)}>취소</ExitCancel>
          </ExitBox>
        </ExitOverlay>
      )}
    </Wrap>
  );
};

export default KioskPage;

const Wrap = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const Top = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const TopTitle = styled.p`
  font-size: 19px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ExitBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 12px;
  background: #F2F3F7;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 700;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: 22px;
  max-width: 520px;
  width: 100%;
  margin: 0 auto;
`;

const Hint = styled.p`
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Display = styled.div`
  display: flex;
  gap: 14px;
`;

const Dot = styled.span`
  width: 52px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F2F3F7')};
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.primary : 'transparent')};
`;

const NoMatch = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent.red};
  padding: 14px 0;
`;

const Matched = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 18px 0;
`;

const MatName = styled.p`
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActBtn = styled.button`
  width: 130px;
  padding: 18px 0;
  border-radius: 16px;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  background: ${({ $kind }) => ($kind === 'in' ? '#4A7CFE' : '#6B7280')};
  &:disabled { opacity: 0.5; }
`;

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
`;

const ChoiceTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 6px;
`;

const ChoiceBtn = styled.button`
  min-width: 200px;
  padding: 14px 22px;
  border-radius: 13px;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  background: #F2F3F7;
`;

const Pad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 360px;
`;

const Key = styled.button`
  height: 72px;
  border-radius: 16px;
  font-size: 26px;
  font-weight: 700;
  color: ${({ $special, theme }) => ($special ? theme.colors.text.secondary : theme.colors.text.primary)};
  background: ${({ $special }) => ($special ? '#EDEEF2' : '#F4F5F7')};
  &:active { background: #DCDEE6; }
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Toast = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px 30px;
  border-radius: 18px;
  background: ${({ $ok }) => ($ok ? '#22C55E' : '#EF4444')};
  color: #fff;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.22);
  z-index: 100;
`;

const ToastMsg = styled.p`
  font-size: 19px;
  font-weight: 800;
`;

const ExitOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 250;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ExitBox = styled.div`
  width: 100%;
  max-width: 340px;
  background: #fff;
  border-radius: 18px;
  padding: 24px 22px;
`;

const ExitTitle = styled.p`
  font-size: 17px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 14px;
`;

const PinInputs = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 6px 0 14px;
`;

const PinInput = styled.input`
  width: 56px;
  height: 64px;
  border: 1.5px solid #E5E7EB;
  border-radius: 12px;
  background: #F4F5F7;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    background: #fff;
  }
`;

const PinHint = styled.p`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  text-align: center;
  margin-bottom: 14px;
  line-height: 1.5;
`;

const ExitError = styled.p`
  margin-bottom: 10px;
  font-size: 13px;
  text-align: center;
  color: ${({ theme }) => theme.colors.accent.red};
`;

const ExitCancel = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 11px;
  background: #F2F3F7;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14.5px;
  font-weight: 700;
`;
